import { useState, useEffect } from 'react';
import { fetchStudioOverallOverviewKeyMetricsData } from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall';
import DataTypeUtils from '@gp/utils/DataTypeUtils';
// @ts-ignore
import { isEmpty } from '@gp/utils/utils';

// Total Studio Counts卡片 5个固定展示的studio name
const FIXED_STUDIO_NAMES = ['Saturn', 'Astra', 'Nexus', 'Amplifier', 'Solar', 'Fuel'];

export default function useKeyMetrics() {
  const [isLoading, setIsLoading] = useState(false);

  // Total Studio Counts卡片的数据
  const [totalStudioCounts, setTotalStudioCounts] = useState<ITotalStudioCounts>({
    total: 0,
    details: [],
  });

  // Newly Acquired Counts卡片的数据
  const [newlyAcquiredCounts, setNewlyAcquiredCounts] = useState<INewlyAcquiredCounts>({
    value: 0,
    logos: [],
  });

  // Number Releases This Year卡片的数据
  const [numberReleasesThisYear, setNumberReleasesThisYear] = useState<INumberReleasesThisYear>({
    value: 0,
    logos: [],
  });

  // Gross Revenue YTM卡片的数据
  const [grossRevenueYTM, setGrossRevenueYTM] = useState<IActualBudgetCompletion>({
    value: 0,
    ratio: 0,
    actualOfLastYear: 0,
    budgetOfThisYear: 0,
    completion: 0,
    dataTime: '',
  });

  // Profit YTM卡片的数据
  const [profitYTM, setProfitYTM] = useState<IActualBudgetCompletion>({
    value: 0,
    ratio: 0,
    actualOfLastYear: 0,
    budgetOfThisYear: 0,
    completion: 0,
    dataTime: '',
  });

  // Total Fulltime Employee卡片的数据
  const [totalFulltimeEmployee, setTotalFulltimeEmployee] = useState<ITotalFulltimeEmployee>({
    value: 0,
    ratio: 0,
    hiringOfLastYear: 0,
    hiringOfThisYear: 0,
    dataTime: '',
  });

  const getTotalStudioCountsCompData = (list: any[]) => {
    if (!DataTypeUtils.isArray(list))
      return {
        total: 0,
        details: [],
      };

    // 所有的total
    let total = 0;
    // others的total
    let othersTotal = 0;
    // others数据对象
    let others = null;
    // 5个固定的studio 小写名称，用于比较、判断
    const fixedStudioNamesLowerCase = FIXED_STUDIO_NAMES.map((name) => name?.toLowerCase());
    const details = FIXED_STUDIO_NAMES.map((studioName) => ({
      text: studioName,
      value: 0,
    }));
    let detailsOptions = [];

    for (const item of list) {
      const itemName = item?.name?.toLowerCase();
      let numTemp = Number.parseInt(item?.value, 10);
      numTemp = Number.isNaN(numTemp) ? 0 : numTemp;
      // 计算所有的total
      total += numTemp;
      // 五个固定的studio
      if (fixedStudioNamesLowerCase.includes(itemName)) {
        const targetIndex = details?.findIndex((subItem) => subItem?.text?.toLowerCase() === itemName);
        if (targetIndex !== -1) {
          details[targetIndex].value += numTemp;
        }
      } else {
        // others
        othersTotal += numTemp;
      }
    }

    details.sort((a, b) => {
      const aName: string = a?.text || '';
      const bName: string = b?.text || '';
      return aName?.toLowerCase()?.localeCompare(bName?.toLowerCase());
    });

    if (othersTotal) {
      others = { text: 'Others', value: othersTotal };
      details.push(others);
    }

    detailsOptions = details.map((item) => ({
      ...item,
      value: String(item?.value),
    }));

    return {
      total,
      details: detailsOptions,
    };
  };

  const getLogoListCompData = (data: any) => {
    if (!DataTypeUtils.isArray(data))
      return {
        value: 0,
        logos: [],
      };
    return {
      value: data?.length || 0,
      logos: data?.map((t: any) => ({
        ...t,
        date: t?.launch_time,
        dateConfirm: t?.launch_date_confirm,
      })),
    };
  };

  const getActualAndBudgetCompData = (data: any): IActualBudgetCompletion => ({
    value: data?.Total,
    ratio: data?.Ratio,
    actualOfLastYear: data?.LastYearActual,
    budgetOfThisYear: data?.ThisYearBudget,
    completion: data?.Completion,
    dataTime: data?.Date,
  });

  const initState = async () => {
    try {
      setIsLoading(true);
      const res: any = await fetchStudioOverallOverviewKeyMetricsData();
      const totalStudioCounts = getTotalStudioCountsCompData(res?.total_studio_counts);
      const newlyAcquiredCounts = getLogoListCompData(res?.newly_acquired_counts);
      const numberReleasesThisYear = getLogoListCompData(res?.number_releases_this_year);
      const grossRevenueYTM = getActualAndBudgetCompData(res?.gross_revenue_ytm);
      const profitYTM = getActualAndBudgetCompData(res?.profit_ytm);
      const totalFulltimeEmployeeRes = res?.total_fulltime_employee || {};
      const totalFulltimeEmployee = {
        value: isEmpty(totalFulltimeEmployeeRes?.total) ? '-' : String(totalFulltimeEmployeeRes?.total),
        ratio: totalFulltimeEmployeeRes?.ratio,
        hiringOfLastYear: totalFulltimeEmployeeRes?.last_year_FTE,
        hiringOfThisYear: totalFulltimeEmployeeRes?.this_year_new_hiring,
        dataTime: totalFulltimeEmployeeRes?.date,
      };
      setTotalStudioCounts(totalStudioCounts);
      setNewlyAcquiredCounts(newlyAcquiredCounts);
      setNumberReleasesThisYear(numberReleasesThisYear);
      setGrossRevenueYTM(grossRevenueYTM);
      setProfitYTM(profitYTM);
      setTotalFulltimeEmployee(totalFulltimeEmployee);
    } catch (exception) {
      console.warn(exception);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initState();
  }, []);

  return {
    isLoading,
    totalStudioCounts,
    newlyAcquiredCounts,
    numberReleasesThisYear,
    grossRevenueYTM,
    profitYTM,
    totalFulltimeEmployee,
  };
}
